import { NavLink } from 'react-router-dom';
import clsx from 'clsx';
import {
  HomeIcon,
  ChartBarIcon,
  BanknotesIcon,
  FlagIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  BuildingLibraryIcon,
  BellIcon,
  ArrowUpTrayIcon,
  XMarkIcon,
  BuildingOffice2Icon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';

const navigation = [
  { name: 'Dashboard',     href: '/dashboard',    icon: HomeIcon },
  { name: 'Ativos',        href: '/assets',        icon: ChartBarIcon },
  { name: 'Proventos',     href: '/dividends',     icon: BanknotesIcon },
  { name: 'Metas',         href: '/goals',         icon: FlagIcon },
  { name: 'Calendário',    href: '/buy-calendar',  icon: CalendarDaysIcon },
  { name: 'Criptomoedas',  href: '/crypto',        icon: CurrencyDollarIcon },
  { name: 'Fundos',        href: '/funds',         icon: BuildingLibraryIcon },
  { name: 'Alertas',       href: '/alerts',        icon: BellIcon },
  { name: 'Importar B3',  href: '/import',        icon: ArrowUpTrayIcon },
];

const adminNavigation = [
  { name: 'Tenants',  href: '/admin/tenants', icon: BuildingOffice2Icon },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { user, logout, isSuperAdmin } = useAuth();

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:w-64 lg:z-30">
        <SidebarContent user={user} logout={logout} isSuperAdmin={isSuperAdmin} />
      </aside>

      {/* Mobile sidebar */}
      <aside
        className={clsx(
          'fixed inset-y-0 left-0 w-64 z-30 transform transition-transform duration-200 lg:hidden',
          isOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="absolute top-4 right-4">
          <button
            onClick={onClose}
            className="btn-ghost p-2 rounded-lg"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <SidebarContent user={user} logout={logout} isSuperAdmin={isSuperAdmin} />
      </aside>
    </>
  );
}

function SidebarContent({
  user,
  logout,
  isSuperAdmin,
}: {
  user: { name: string; email: string } | null;
  logout: () => void;
  isSuperAdmin: boolean;
}) {
  return (
    <div className="flex flex-col h-full bg-surface-800 border-r border-white/5 relative overflow-hidden">

      {/* ── SVG watermark — símbolo InoveiTech sem texto ── */}
      <svg
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 400 305"
        className="absolute bottom-0 left-1/2 -translate-x-1/2 pointer-events-none select-none"
        style={{ width: '280px', height: 'auto', opacity: 0.055 }}
      >
        {/* Rings */}
        <circle cx="200" cy="168" r="118" fill="none" stroke="#1a3a5c" strokeWidth="1.5"/>
        <circle cx="200" cy="168" r="88"  fill="none" stroke="#1a3a5c" strokeWidth="1.5"/>
        <circle cx="200" cy="168" r="58"  fill="none" stroke="#1a3a5c" strokeWidth="1.5"/>
        {/* Hexagon */}
        <polygon points="200,82 262,118 262,190 200,226 138,190 138,118"
          fill="none" stroke="#00d4ff" strokeWidth="1.2" opacity="0.3"/>
        {/* 6 gray structural lines */}
        <line x1="200" y1="50"  x2="200" y2="110" stroke="#1a3a5c" strokeWidth="1.5"/>
        <line x1="200" y1="226" x2="200" y2="286" stroke="#1a3a5c" strokeWidth="1.5"/>
        <line x1="86"  y1="109" x2="138" y2="138" stroke="#1a3a5c" strokeWidth="1.5"/>
        <line x1="314" y1="109" x2="262" y2="138" stroke="#1a3a5c" strokeWidth="1.5"/>
        <line x1="86"  y1="227" x2="138" y2="198" stroke="#1a3a5c" strokeWidth="1.5"/>
        <line x1="314" y1="227" x2="262" y2="198" stroke="#1a3a5c" strokeWidth="1.5"/>
        {/* 3 active cyan lines */}
        <line x1="200" y1="50"  x2="200" y2="105" stroke="#00d4ff" strokeWidth="2" opacity="0.8"/>
        <line x1="314" y1="109" x2="266" y2="136" stroke="#00d4ff" strokeWidth="2" opacity="0.8"/>
        <line x1="86"  y1="227" x2="134" y2="200" stroke="#00d4ff" strokeWidth="2" opacity="0.8"/>
        {/* Main nodes (solid) */}
        <circle cx="200" cy="48"  r="14" fill="#00d4ff" opacity="0.15"/>
        <circle cx="200" cy="48"  r="6"  fill="#00d4ff"/>
        <circle cx="316" cy="107" r="14" fill="#00d4ff" opacity="0.15"/>
        <circle cx="316" cy="107" r="6"  fill="#00d4ff"/>
        <circle cx="84"  cy="229" r="14" fill="#00d4ff" opacity="0.15"/>
        <circle cx="84"  cy="229" r="6"  fill="#00d4ff"/>
        {/* Secondary nodes (dim) */}
        <circle cx="200" cy="288" r="10" fill="#00d4ff" opacity="0.15"/>
        <circle cx="200" cy="288" r="4"  fill="#00d4ff" opacity="0.4"/>
        <circle cx="316" cy="229" r="10" fill="#00d4ff" opacity="0.15"/>
        <circle cx="316" cy="229" r="4"  fill="#00d4ff" opacity="0.4"/>
        <circle cx="84"  cy="107" r="10" fill="#00d4ff" opacity="0.15"/>
        <circle cx="84"  cy="107" r="4"  fill="#00d4ff" opacity="0.4"/>
        {/* AI core */}
        <circle cx="200" cy="168" r="44" fill="#00d4ff" opacity="0.07"/>
        <circle cx="200" cy="168" r="30" fill="#00d4ff" opacity="0.10"/>
        <circle cx="200" cy="168" r="18" fill="#00d4ff" opacity="0.90"/>
        <text x="200" y="175" textAnchor="middle" fill="#0a0f1e"
          fontFamily="system-ui, sans-serif" fontSize="20" fontWeight="800">AI</text>
      </svg>

      {/* ── Logo text ── */}
      <div className="relative z-10 flex items-center gap-2.5 px-5 py-4 border-b border-white/5">
        <div style={{
          width: 28, height: 28, borderRadius: 8, flexShrink: 0,
          background: 'rgba(0,212,255,0.12)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <span style={{ fontSize: 11, fontWeight: 900, color: '#00d4ff' }}>IT</span>
        </div>
        <div>
          <p style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.25 }}>
            <span style={{ color: '#fff' }}>Incluir</span>
            <span style={{ color: '#00d4ff' }}>Tech</span>
          </p>
          <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.28)', marginTop: 1 }}>
            Investimentos
          </p>
        </div>
      </div>

      {/* Navigation */}
      <nav className="relative z-10 flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {navigation.map((item) => (
          <NavLink
            key={item.name}
            to={item.href}
            className={({ isActive }) =>
              clsx(
                'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                isActive
                  ? 'bg-primary-600/20 text-primary-400'
                  : 'text-white/60 hover:text-white hover:bg-white/5',
              )
            }
          >
            <item.icon className="h-5 w-5 flex-shrink-0" />
            {item.name}
          </NavLink>
        ))}

        {/* Seção Admin (somente superadmin) */}
        {isSuperAdmin && (
          <>
            <div className="pt-4 pb-1 px-3">
              <div className="flex items-center gap-2">
                <ShieldCheckIcon className="h-3.5 w-3.5 text-[#00d4ff]/50" />
                <span className="text-[10px] font-semibold text-[#00d4ff]/50 uppercase tracking-widest">
                  Administração
                </span>
              </div>
            </div>
            {adminNavigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-[#00d4ff]/10 text-[#00d4ff]'
                      : 'text-white/50 hover:text-white hover:bg-white/5',
                  )
                }
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                {item.name}
              </NavLink>
            ))}
          </>
        )}
      </nav>

      {/* User profile */}
      <div className="relative z-10 px-4 py-4 border-t border-white/5">
        <div className="flex items-center gap-3 px-2">
          <div
            className="h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: isSuperAdmin ? 'rgba(0,212,255,0.15)' : 'rgba(59,130,246,0.2)' }}
          >
            <span
              className="text-xs font-bold"
              style={{ color: isSuperAdmin ? '#00d4ff' : 'rgb(96,165,250)' }}
            >
              {user?.name?.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-white truncate">{user?.name}</p>
            <p className="text-xs truncate" style={{ color: isSuperAdmin ? 'rgba(0,212,255,0.5)' : 'rgba(255,255,255,0.4)' }}>
              {isSuperAdmin ? '⚡ superadmin' : user?.email}
            </p>
          </div>
        </div>
        <button
          onClick={logout}
          className="mt-3 w-full btn-ghost text-xs justify-start px-2"
        >
          Sair da conta
        </button>
      </div>
    </div>
  );
}
